import { Box, Stack, Typography, Divider, IconButton, Tooltip, Collapse } from '@mui/material'
import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import DOMPurify from 'dompurify'

import Logo from "../../assets/Images/logo.ico"
import { Copy, ThumbsDown, ThumbsUp, CaretDown, CaretUp, Info, Warning, XCircle } from 'phosphor-react'
import { sendFeedback } from '../../utils/api'
import PostpaidForm from '../TestDataForm/Postpaid/PostpaidForm'
import PrepaidForm from '../TestDataForm/Prepaid/PrepaidForm'
import ExampleForm from '../TestDataForm/ReferenceForm/ExampleForm'
import StoreidExp from '../TestDataForm/ReferenceForm/StoreidExp'
import MaterialcodeExp from '../TestDataForm/ReferenceForm/MaterialcodeExp'

const PromptMsg = ({ el }) => {
  return (
    <>
      <Box sx={{ py: 2.5, px: 1 }}>
        <Typography variant="h4">
          {el.err_message || el.message}
        </Typography>
      </Box>
    </>
  )
}

const AgentProgressMsg = ({ el }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get progress details from element, or use empty array
  const progressDetails = el.progressDetails;

  // Get the last message type for collapsed view
  const lastMessage = progressDetails[progressDetails.length - 1];

  // Helper function to get icon and color based on message type
  const getMessageIcon = (type) => {
    switch (type) {
      case "info":
        return <Info size={18} weight="fill" color="#0288d1" />;
      case "warning":
        return <Warning size={18} weight="fill" color="#f57c00" />;
      case "error":
        return <XCircle size={18} weight="fill" color="#d32f2f" />;
      default:
        return <Info size={18} weight="fill" color="#757575" />;
    }
  };

  // Helper function to get background color based on message type
  const getBackgroundColor = (type) => {
    switch (type) {
      case "info":
        return "#e3f2fd";
      case "warning":
        return "#fff3e0";
      case "error":
        return "#ffebee";
      default:
        return "#f5f5f5";
    }
  };

  // Helper function to get border color based on message type
  const getBorderColor = (type) => {
    switch (type) {
      case "info":
        return "#0288d1";
      case "warning":
        return "#f57c00";
      case "error":
        return "#d32f2f";
      default:
        return "#bdbdbd";
    }
  };

  if (!progressDetails || progressDetails.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          p: 1.5,
          mb: 1,
          backgroundColor: getBackgroundColor(lastMessage?.type),
          borderRadius: 2,
          border: `1px solid ${getBorderColor(lastMessage?.type)}`,
          cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
          }
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {getMessageIcon(lastMessage?.type)}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
            Agent Progress ({progressDetails.length} message{progressDetails.length !== 1 ? 's' : ''})
          </Typography>
          <IconButton size="small" sx={{ p: 0 }}>
            {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
          </IconButton>
        </Stack>

        <Collapse in={isExpanded}>
          <Divider sx={{ my: 1 }} />
          <Stack direction="column" spacing={1} mt={1}>
            {progressDetails.map((detail, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3 }}>
                  {getMessageIcon(detail.type)}
                </Box>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {detail.message}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Collapse>
      </Box>
    </>
  );
}

const AnswerMsg = ({ el }) => {
  const [selectedForm, setSelectedForm] = useState(null);

  // Early return for special message types
  if (!el || !el.message) {
    return null;
  }

  // Handle ticket-related responses
  const isTicketResponse = el.type === 'sr_ticket_success' ||
    el.type === 'sr_ticket_error' ||
    el.action === 'sr_ticket_success' ||
    el.action === 'sr_ticket_error' ||
    el.action === 'getSRTicket' ||
    Boolean(el.ticket_number) ||
    Boolean(el.ticketno) ||
    Boolean(el.error);

  if (isTicketResponse) {
    return <TicketResponseMsg el={el} />;
  }

  const message = el.err_message || el.message || '';
  const sanitizedHtml = DOMPurify.sanitize(
    message
      .replace(/\\n/g, '\n') // Convert escaped \n to actual line breaks
      .replace('[postpaid_form]', '')
      .replace('[prepaid_form]', '')
      .replace('[example_test_data_form]', '')
      .replace('[store_id_exp]', '')
      .replace('[material_code_exp]', '')
  );

  const formPlaceholders = {
    '[example_test_data_form]': ExampleForm,
    '[store_id_exp]': StoreidExp,
    '[material_code_exp]': MaterialcodeExp,
    '[postpaid_form]': PostpaidForm,
    // '[prepaid_form]': PrepaidForm
  };

  const formToRender = Object.entries(formPlaceholders).find(([placeholder, _]) => 
    el.message && el.message.includes(placeholder)
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(el.message);
      console.log('Message copied to clipboard');
      await sendFeedback('copy', el.message_id);
    } catch (err) {
      console.error('Failed to copy message: ', err);
    }
  };

  const handleGoodResponse = async () => {
    try {
      await sendFeedback('good_response', el.message_id);
      console.log('Good response feedback sent');
    } catch (err) {
      console.error('Failed to send good response feedback: ', err);
    }
  };

  const handleReport = async () => {
    try {
      await sendFeedback('report', el.message_id);
      console.log('Report feedback sent');
    } catch (err) {
      console.error('Failed to send report feedback: ', err);
    }
  };

  // Custom renderer for links
  const renderers = {
    a: ({ node, ...props }) => (
      <a target="_blank" rel="noopener noreferrer" {...props} />
    ),
    table: ({ children }) => (
      <div style={{ 
        overflow: 'auto',
        margin: '10px 0',
        padding: '10px',
        border: '1px solid #ddd'
      }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          borderSpacing: 0
        }}>
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th style={{
        padding: '12px',
        backgroundColor: '#f5f5f5',
        borderBottom: '2px solid #ddd',
        textAlign: 'left'
      }}>
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td style={{
        padding: '8px',
        borderBottom: '1px solid #ddd'
      }}>
        {children}
      </td>
    )
  };

  return (
    <>
      <Stack direction="column" padding={1} spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <img
            alt={"Logo"}
            src={Logo}
            style={{
              height: 35,
              width: 35
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 550 }}>
            Answer
          </Typography>
        </Stack>

        <ReactMarkdown components={renderers} rehypePlugins={[rehypeRaw]}>
          {sanitizedHtml}
        </ReactMarkdown>

        {(el.message.includes('[postpaid_form]') || el.message.includes('[prepaid_form]')) && !selectedForm && (
          <Stack direction="row" spacing={2} mt={2}>
            <button onClick={() => setSelectedForm('postpaid')}>Postpaid Form</button>
            <button onClick={() => setSelectedForm('prepaid')}>Prepaid Form</button>
          </Stack>
        )}

        {selectedForm === 'postpaid' && <PostpaidForm />}
        {selectedForm === 'prepaid' && <PrepaidForm />}

        {/* render individual form */}
        {formToRender && React.createElement(formToRender[1])}
      </Stack>

      <Box
        p={1}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <Tooltip title="Copy" arrow placement="top">
          <IconButton size="small" onClick={handleCopy}>
            <Copy size={20} color="#3A555A" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Good Response" arrow placement="top">
          <IconButton size="small" onClick={handleGoodResponse}>
            <ThumbsUp size={20} color="#3A555A" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Report" arrow placement="top">
          <IconButton size="small" onClick={handleReport}>
            <ThumbsDown size={20} color="#3A555A" />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ m: 1 }} />
    </>
  );
};

// Ticket response messages with borders
const TicketResponseMsg = ({ el }) => {
  if (!el) return null;
  
  const timestamp = new Date().toLocaleTimeString();
  
  // Extract message and ticket number from various possible locations in the response
  const ticketNumber = el.ticket_number || el.ticketno;
  const message = el.message || el.success_message || el.err_message;
  
  // Success message
  const isSuccess = el.action === 'sr_ticket_success' || 
                   el.type === 'sr_ticket_success' || 
                   ticketNumber;
                   
  if (isSuccess) {
    return (
      <Box
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: '#e8f5e9',
          borderRadius: 2,
          border: '1px solid #2e7d32',
          position: 'relative',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="caption" sx={{ position: 'absolute', top: 8, right: 16, color: 'text.secondary' }}>
          {timestamp}
        </Typography>
        <Typography variant="subtitle2" color="#1b5e20" sx={{ mb: 1, fontWeight: 'bold' }}>
          Ticket Status: Success
        </Typography>
        <Typography variant="body1" color="#2e7d32">
          {message || `Ticket created successfully! Ticket Number: ${ticketNumber}`}
        </Typography>
      </Box>
    );
  }

  // Error message
  const isError = el.type === "sr_ticket_error" || 
                 el.action === "sr_ticket_error" || 
                 el.error;
                 
  if (isError) {
    return (
      <Box
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: '#ffebee',
          borderRadius: 2,
          border: '1px solid #d32f2f',
          position: 'relative',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="caption" sx={{ position: 'absolute', top: 8, right: 16, color: 'text.secondary' }}>
          {timestamp}
        </Typography>
        <Typography variant="subtitle2" color="#c62828" sx={{ mb: 1, fontWeight: 'bold' }}>
          Ticket Status: Error
        </Typography>
        <Typography variant="body1" color="#d32f2f">
          {message || el.error || 'An error occurred while creating the ticket'}
        </Typography>
      </Box>
    );
  }

  return null;
};

export { AnswerMsg, PromptMsg, AgentProgressMsg, TicketResponseMsg };